import { ArrowForwardIcon, CloseIcon, DownloadIcon } from '@chakra-ui/icons'
import { Box, Center, Divider, Flex, IconButton, Link, Text } from '@chakra-ui/react'
import { ConsentForm, WithId } from '@hb/shared'
import { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useConsentFormDownloadUpload } from '../../hooks/consentForms/useConsentFormDownloadUpload'
import { SolidActionButton } from '../Buttons'
import { DefaultModal } from '../Modals'

interface DocumentFormTroubleSigningContentProps {
  document?: WithId<ConsentForm>
  onClose: () => void
}

const DownloadFormSection = ({
  document,
  onClose,
}: {
  document: WithId<ConsentForm>
  onClose: () => void
}) => {
  const { isDownloading, handleDownload } = useConsentFormDownloadUpload(document)

  const handleDownloadClick = () => {
    handleDownload()
  }
  return (
    <Flex flexDirection="column" gap={4}>
      {/* Step 1 */}
      <Divider />
      <Flex flexDirection="column" gap={2}>
        <Text fontWeight={600} fontSize="sm" color="gray.700">
          Step 1: Download the unsigned consent form
        </Text>
        <Text color="gray.600">Click the button below to download the consent form as a PDF.</Text>
        <Box>
          <SolidActionButton
            size="sm"
            leftIcon={<DownloadIcon filter="drop-shadow(0 0 3px #00000066)" />}
            onClick={handleDownloadClick}
            isLoading={isDownloading}
            isDisabled={isDownloading}>
            Download Unsigned Form
          </SolidActionButton>
        </Box>
      </Flex>
      <Divider />

      {/* Step 2 */}
      <Flex flexDirection="column" gap={2}>
        <Text fontWeight={600} fontSize="sm" color="gray.700">
          Step 2: Sign the form
        </Text>
        <Text fontSize="sm" color="gray.600">
          Sign the form by printing it out and signing it with a pen, or use a PDF signing software
          or app to add your signature digitally.
        </Text>
      </Flex>
      <Divider />
      {/* Step 3 */}
      <Flex flexDirection="column" gap={2}>
        <Text fontWeight={600} fontSize="sm" color="gray.700">
          Step 3: Upload the signed consent form
        </Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Go to the Patient Files page to upload your signed consent form.
        </Text>
        <Box>
          <Link as={RouterLink} to="/files" style={{ opacity: 1 }} onClick={onClose}>
            <SolidActionButton
              rightIcon={<ArrowForwardIcon w={4} h={4} filter="drop-shadow(0 0 3px #00000066)" />}
              size="sm">
              Go to Patient Files Page
            </SolidActionButton>
          </Link>
        </Box>
      </Flex>
    </Flex>
  )
}

const DownloadInstructionsSection = () => (
  <Flex flexDirection="column">
    <Text fontSize="md" color="gray.700">
      You can sign and upload the form manually by opening your consent form and clicking the
      "Having trouble signing your form?" button.
    </Text>
  </Flex>
)

const DocumentFormTroubleSigningContent = ({
  document,
  onClose,
}: DocumentFormTroubleSigningContentProps) => (
  <Box w="100%">
    <Flex align="center" w="100%" justify="space-between">
      <Text fontWeight={500} fontFamily="Hero-New" fontSize="lg">
        Having trouble signing your form?
      </Text>
      <IconButton
        aria-label="Close"
        borderRadius="full"
        w={7}
        h={7}
        size="xs"
        colorScheme="gray"
        color="gray.700"
        onClick={onClose}
        icon={<CloseIcon />}
      />
    </Flex>
    <Box mb={4} mt={2}>
      <Text color="gray.700" mb={3}>
        Before downloading and signing offline, try these quick fixes:
      </Text>
      <Flex flexDirection="column" gap={2}>
        <Text color="gray.700">
          <Text as="span" fontWeight={600}>
            1. Are you on a phone?{' '}
          </Text>
          If so, try opening this form on a desktop or laptop — phone browsers often have trouble
          with document signing.
        </Text>
        <Text color="gray.700">
          <Text as="span" fontWeight={600}>
            2. Which browser are you using?{' '}
          </Text>
          Chrome and Safari tend to work best. If you&apos;re on Firefox, Edge, or another browser,
          try switching to Chrome or Safari and reloading the page.
        </Text>
        <Text color="gray.700">
          <Text as="span" fontWeight={600}>
            3. Is your browser up to date?{' '}
          </Text>
          An outdated browser can cause signing issues. Check your browser settings to make sure
          you&apos;re on the latest version, then try again.
        </Text>
      </Flex>
    </Box>
    <Divider mb={4} />
    <Box>
      <Text fontWeight={600} color="gray.700" _dark={{ color: 'gray.600' }} fontSize="md" mb={2}>
        Still having trouble? Sign and upload the form manually:
      </Text>
      {document ? (
        <DownloadFormSection document={document} onClose={onClose} />
      ) : (
        <DownloadInstructionsSection />
      )}
    </Box>
  </Box>
)

interface DocumentFormTroubleSigningOverlayProps {
  isOpen: boolean
  onClose: () => void
  document: WithId<ConsentForm> | undefined
  width: number
  height: number
}

export const DocumentFormTroubleSigningOverlay = ({
  isOpen,
  onClose,
  document,
  width,
  height,
}: DocumentFormTroubleSigningOverlayProps) => {
  const modalWidth = useMemo(() => Math.min(700, width), [width])
  const modalHeight = useMemo(() => Math.min(800, height * 0.95), [height])
  if (!document) return null

  return (
    <Center
      position="absolute"
      opacity={isOpen ? 1 : 0}
      alignItems="center"
      justifyContent="center"
      zIndex={3}
      pointerEvents={isOpen ? 'auto' : 'none'}
      transition="opacity 0.3s ease-in-out"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0,0,0,0.5)">
      <Flex
        w={`${modalWidth}px`}
        bg="white"
        borderRadius={6}
        userSelect="none"
        p={4}
        maxHeight={`${modalHeight}px`}
        flexFlow="column"
        overflowY="auto"
        gap={0}>
        <DocumentFormTroubleSigningContent document={document} onClose={onClose} />
      </Flex>
    </Center>
  )
}

export interface DocumentFormTroubleSigningModalProps {
  isOpen: boolean
  onClose: () => void
  document: WithId<ConsentForm> | undefined
}

export const DocumentFormTroubleSigningModal = ({
  isOpen,
  onClose,
  document,
}: DocumentFormTroubleSigningModalProps) => (
  <DefaultModal
    overlayHeader
    isOpen={isOpen}
    noCloseButton
    bodyProps={{ p: 4 }}
    onClose={onClose}
    render={() => <DocumentFormTroubleSigningContent document={document} onClose={onClose} />}
  />
)
